import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";

const steps = [
  {
    num: "01",
    title: "Concept to Frame",
    desc: "Director briefs are converted into cinematic keyframes, prompt systems, look references, and shot breakdowns that can move quickly into discussion and execution.",
  },
  {
    num: "02",
    title: "AI Previz",
    desc: "Sequence-level visual previews for action beats, environments, creatures, camera ideas, staging options, and edit placeholders.",
  },
  {
    num: "03",
    title: "Frame Overlay Method",
    desc: "Single-frame Procreate overlays guide AI generations for accurate set extension, VFX placement, composition matching, and fast director approval.",
  },
  {
    num: "04",
    title: "Photoreal Continuity",
    desc: "Camera angle, lens feel, lighting direction, actor identity, costume, material detail, and scene continuity are preserved across image-to-image workflows.",
  },
];

export default function PipelineSection() {
  return (
    <section
      id="pipeline"
      data-testid="pipeline-section"
      className="py-24 sm:py-32 px-6 sm:px-12"
    >
      <div className="max-w-7xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7 }}
        >
          <p className="section-number mb-4">03 &middot; AI-VFX Pipeline</p>
          <p className="text-base text-neutral-400 mb-2 max-w-xl">
            Concept &rarr; Keyframe &rarr; Previz &rarr; Shot Plan
          </p>
          <p className="text-sm text-neutral-500 max-w-xl">
            A production-first workflow designed for speed, continuity, and
            cinematic believability.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-16">
          {steps.map((step, i) => (
            <motion.div
              key={step.num}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{
                duration: 0.6,
                delay: i * 0.12,
                ease: [0.16, 1, 0.3, 1],
              }}
              data-testid={`pipeline-step-${i}`}
              className="glass-surface glass-surface-hover rounded-2xl p-8 transition-all duration-500 relative group"
            >
              <span className="font-heading text-5xl font-extralight text-white/5 absolute top-4 right-6 group-hover:text-white/10 transition-colors duration-500">
                {step.num}
              </span>
              <p className="section-number mb-4">Pipeline {step.num}</p>
              <h3 className="font-heading text-xl tracking-tight text-white mb-3">
                {step.title}
              </h3>
              <p className="text-sm text-neutral-400 leading-relaxed">
                {step.desc}
              </p>
              {i < steps.length - 1 && (
                <ArrowRight
                  size={16}
                  className="text-amber-500/40 absolute -right-2 top-1/2 -translate-y-1/2 hidden lg:block"
                />
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
